<script setup>
import { onBeforeUnmount, ref } from 'vue';
import PgeEmojiPicker from '../../pge-components/pge-emoji-picker.vue';
import { PICKER_DISABLED, PICKER_GROUP_NAMES, pickerGroups, rememberEmoji } from '../../helpers/messageEmoji';

const emit = defineEmits(['pick', 'close']);

const button = ref(null);
const rect = ref(null);
// Read once per opening, so Recent does not reshuffle under the pointer.
const groups = ref({});

// On the document: a pick hands focus back to the field it wrote into, and Escape must still close it from there.
const escape = (event) => {
  if (event.key === 'Escape') close();
};

function close() {
  if (!rect.value) return;
  rect.value = null;
  document.removeEventListener('keydown', escape);
  emit('close');
}

const toggle = () => {
  if (rect.value) return close();
  groups.value = pickerGroups();
  rect.value = button.value.getBoundingClientRect();
  document.addEventListener('keydown', escape);
};

const select = (emoji) => {
  rememberEmoji(emoji.i);
  emit('pick', emoji.i);
};

onBeforeUnmount(() => document.removeEventListener('keydown', escape));
</script>

<template>
  <button ref="button" type="button" :class="{ on: rect }" aria-haspopup="dialog" :aria-expanded="!!rect" @click.stop="toggle">
    <slot />
    <PgeEmojiPicker
      v-if="rect"
      persistent
      initial="top"
      x-position="right"
      :rect="rect"
      :additional-groups="groups"
      :group-names="PICKER_GROUP_NAMES"
      :disabled-groups="PICKER_DISABLED"
      hide-search
      hide-group-icons
      disable-skin-tones
      class="msg-emoji"
      data-xl-gen="northstar"
      data-testid="emoji-picker"
      @close="close"
      @select-emoji="select"
    />
  </button>
</template>

<style scoped>
/* Drawn on the body, so it names the Northstar subtree itself; that subtree otherwise fills the page's height. */
:global(.pge-tooltip .v3-emoji-picker.msg-emoji) {
  min-height: 0;
  height: 292px;
  width: 306px;
  padding: 10px 10px 6px;
  border: 1px solid #ede7f0;
  border-radius: 16px;
  box-shadow: 0 18px 44px rgba(28, 23, 32, 0.18);
  color: #1c1720;
}

:global(.pge-tooltip .v3-emoji-picker.msg-emoji .v3-body) { padding: 0; }

:global(.pge-tooltip .v3-emoji-picker.msg-emoji .v3-body .v3-body-inner .v3-group h5) {
  font-size: 12.5px;
  font-weight: 600;
  color: #514a57;
}

:global(.pge-tooltip .v3-emoji-picker.msg-emoji .v3-body .v3-body-inner .v3-group .v3-emojis button) {
  flex-basis: 14.28%;
  max-width: 14.28%;
  border-radius: 10px;
}

:global(.pge-tooltip .v3-emoji-picker.msg-emoji .v3-body .v3-body-inner .v3-group .v3-emojis button:hover) { background: #f5eff9; }

/* The preview line names the library's default emoji, not one offered here. */
:global(.pge-tooltip .v3-emoji-picker.msg-emoji .v3-footer) { display: none; }
</style>
